import type { PropsWithChildren, ReactNode } from "react";
import { cn } from "../cn";
import { useT } from "../../i18n";
import { type ZoneKey } from "./mkt-format";

const ZONE_STYLE: Record<ZoneKey, string> = {
  standard: "text-zone-standard border-zone-standard/40 bg-zone-standard/10",
  premium: "text-zone-premium border-zone-premium/40 bg-zone-premium/10",
  vip: "text-zone-vip border-zone-vip/50 bg-zone-vip/10",
  reserve: "text-zone-reserve border-zone-reserve/40 bg-zone-reserve/10",
  grid_zero: "text-neon-coral border-border-coral bg-neon-coral/10",
  unknown: "text-text-muted border-border-divider bg-elevated/40",
};

const ZONE_KEY_MAP: Record<Exclude<ZoneKey, "unknown">, string> = {
  standard: "standard",
  premium: "premium",
  vip: "vip",
  reserve: "reserve",
  grid_zero: "gridZero",
};

export function ZoneBadge({ zone, testId }: { zone: ZoneKey; testId?: string }) {
  const t = useT();
  if (zone === "unknown") return null;
  return (
    <span
      data-testid={testId}
      data-zone={zone}
      className={cn(
        "inline-flex items-center h-5 px-2 rounded-sm border",
        "font-mono text-[9.5px] font-semibold tracking-[0.18em] uppercase whitespace-nowrap",
        ZONE_STYLE[zone],
      )}
    >
      {t(`marketplace.zones.${ZONE_KEY_MAP[zone]}`)}
    </span>
  );
}

export function Money({ amount, symbol, className, testId }: { amount: string; symbol: string; className?: string; testId?: string }) {
  return (
    <span data-testid={testId} className={cn("font-display tabular-nums", className)}>
      {amount}
      <span className="ml-1 font-mono text-[0.7em] tracking-[0.14em] text-text-muted uppercase">{symbol}</span>
    </span>
  );
}

const STATE_STYLE: Record<string, string> = {
  active: "text-neon-teal border-neon-teal/40 bg-neon-teal/10",
  sale_pending: "text-state-warning border-state-warning/40 bg-state-warning/10",
  confirming: "text-state-info border-state-info/40 bg-state-info/10",
  sold: "text-text-muted border-border-divider bg-elevated/40",
  expired: "text-text-muted border-border-divider bg-elevated/40",
  cancelled: "text-text-muted border-border-divider bg-elevated/40",
  reconciliation_required: "text-state-danger border-state-danger/50 bg-state-danger/10",
  open: "text-neon-teal border-neon-teal/40 bg-neon-teal/10",
};

const STATE_KEYS = new Set(Object.keys(STATE_STYLE));

export function StatePill({ state, label, testId }: { state: string; label?: string; testId?: string }) {
  const t = useT();
  const key = state.toLowerCase();
  const display = label ?? (STATE_KEYS.has(key) ? t(`marketplace.statePill.${key}`) : state.replace(/_/g, " "));
  return (
    <span
      data-testid={testId}
      data-state={key}
      className={cn(
        "inline-flex items-center h-5 px-2 rounded-sm border",
        "font-mono text-[9.5px] font-semibold tracking-[0.16em] uppercase",
        STATE_STYLE[key] ?? STATE_STYLE.sold,
      )}
    >
      {display}
    </span>
  );
}

export function Tag({ children, tone = "teal", testId }: PropsWithChildren<{ tone?: "teal" | "coral" | "muted"; testId?: string }>) {
  const style =
    tone === "coral"
      ? "text-neon-coral border-border-coral bg-neon-coral/10"
      : tone === "muted"
        ? "text-text-muted border-border-divider bg-elevated/40"
        : "text-neon-teal border-neon-teal/40 bg-neon-teal/10";
  return (
    <span data-testid={testId} className={cn("inline-flex items-center h-5 px-1.5 rounded-sm border font-mono text-[9px] font-semibold tracking-[0.16em] uppercase", style)}>
      {children}
    </span>
  );
}

export function MktButton({
  children,
  onClick,
  variant = "primary",
  disabled,
  testId,
  className,
  type = "button",
}: PropsWithChildren<{
  onClick?: () => void;
  variant?: "primary" | "ghost" | "coral";
  disabled?: boolean;
  testId?: string;
  className?: string;
  type?: "button" | "submit";
}>) {
  const base =
    "inline-flex items-center justify-center gap-2 h-10 px-5 rounded-md font-mono text-[11px] font-semibold tracking-[0.16em] uppercase transition-[background-color,box-shadow,border-color,transform,opacity] duration-fast disabled:opacity-40 disabled:cursor-not-allowed select-none";
  const styles =
    variant === "primary"
      ? "bg-neon-teal/15 text-neon-teal border border-neon-teal/50 hover:bg-neon-teal/25 hover:shadow-glow-teal-sm active:translate-y-px"
      : variant === "coral"
        ? "bg-neon-coral/15 text-neon-coral border border-border-coral hover:bg-neon-coral/25 hover:shadow-glow-coral-sm active:translate-y-px"
        : "bg-transparent text-text-secondary border border-border-divider hover:text-text-primary hover:border-border-strong";
  return (
    <button type={type} data-testid={testId} onClick={onClick} disabled={disabled} className={cn(base, styles, className)}>
      {children}
    </button>
  );
}

export function SectionHeader({ index, title, action, testId }: { index?: string; title: string; action?: ReactNode; testId?: string }) {
  return (
    <div data-testid={testId} className="flex items-end justify-between gap-4 mb-5">
      <div className="flex items-baseline gap-3">
        {index && <span className="font-mono text-[11px] tracking-[0.28em] text-neon-teal/70">{index}</span>}
        <h2 className="font-display text-lg md:text-xl font-semibold tracking-[0.02em] text-text-primary uppercase">{title}</h2>
      </div>
      {action}
    </div>
  );
}

export function EmptyState({ title, hint, testId, icon }: { title: string; hint?: string; testId?: string; icon?: ReactNode }) {
  return (
    <div data-testid={testId} className="neon-glass rounded-lg border border-border-divider py-16 px-6 flex flex-col items-center text-center">
      <div className="mb-3 text-text-faint">{icon}</div>
      <p className="font-display text-base text-text-secondary tracking-wide">{title}</p>
      {hint && <p className="mt-2 font-mono text-[11px] text-text-muted max-w-sm leading-relaxed">{hint}</p>}
    </div>
  );
}

export function ErrorState({ title, detail, onRetry, testId }: { title: string; detail?: string; onRetry?: () => void; testId?: string }) {
  const t = useT();
  return (
    <div data-testid={testId} className="neon-glass rounded-lg border border-state-danger/40 py-12 px-6 flex flex-col items-center text-center">
      <p className="font-display text-base text-state-danger tracking-wide">{title}</p>
      {detail && <p className="mt-2 font-mono text-[11px] text-text-muted max-w-md leading-relaxed break-words">{detail}</p>}
      {onRetry && (
        <div className="mt-4">
          <MktButton variant="ghost" onClick={onRetry} testId="mkt-error-retry">{t("marketplace.common.retry")}</MktButton>
        </div>
      )}
    </div>
  );
}

export function CardSkeleton() {
  return (
    <div className="neon-glass rounded-lg border border-border-divider overflow-hidden animate-pulse">
      <div className="aspect-[4/3] bg-elevated/60" />
      <div className="p-4 space-y-3">
        <div className="h-3 w-2/3 bg-elevated/60 rounded" />
        <div className="h-3 w-1/3 bg-elevated/60 rounded" />
        <div className="h-6 w-full bg-elevated/40 rounded" />
      </div>
    </div>
  );
}

export function Stat({ label, children, testId }: PropsWithChildren<{ label: string; testId?: string }>) {
  return (
    <div data-testid={testId} className="flex flex-col gap-1">
      <span className="font-mono text-[9.5px] tracking-[0.2em] uppercase text-text-muted">{label}</span>
      <span className="font-display text-sm text-text-primary tabular-nums">{children}</span>
    </div>
  );
}
